/**
 * contenteditable のキャレットを UTF-16 の数値として扱う。
 *
 * ## 行を「テキストノード 1 つ」に保つ
 * 行の中身をプレーンなテキスト 1 本に限ると、キャレットの位置が `anchorOffset` そのものになり、
 * 木を歩いて数え上げる処理が丸ごと要らなくなる。装飾（タグの丸窓など）は**行の外側**か、
 * 編集していない行の表示だけに寄せる。編集中の行が単純であることは、IME と相性を取るための
 * 前提でもある——未確定の文字を差し込む先が 1 か所しかない状態にしておく。
 */

/** 行要素の中身を 1 本のテキストノードに正規化して返す。 */
function textNodeOf(el: HTMLElement): Text {
  const first = el.firstChild;
  if (first && first.nodeType === Node.TEXT_NODE && !first.nextSibling) return first as Text;
  const text = el.textContent ?? "";
  el.textContent = text; // 子が複数あれば潰す（貼り付けや保険）
  if (!el.firstChild) el.appendChild(document.createTextNode(""));
  return el.firstChild as Text;
}

/** いまのキャレット位置（UTF-16）。この行に無ければ null。 */
export function caretOffset(el: HTMLElement): number | null {
  const sel = document.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!el.contains(range.startContainer)) return null;
  // 行が空のとき、コンテナは行要素そのものになる。
  if (range.startContainer === el) return range.startOffset === 0 ? 0 : (el.textContent ?? "").length;
  return range.startOffset;
}

/** 選択が潰れている（範囲でなくキャレット）か。 */
export function caretIsCollapsed(): boolean {
  const sel = document.getSelection();
  return !sel || sel.isCollapsed;
}

/** キャレットを置く。行末を超える指定は行末へ丸める。 */
export function setCaret(el: HTMLElement, offset: number): void {
  const node = textNodeOf(el);
  const at = Math.max(0, Math.min(offset, node.length));
  const range = document.createRange();
  range.setStart(node, at);
  range.collapse(true);
  const sel = document.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

/**
 * 行の文字列を差し替える。**中身が同じなら DOM に触らない**。
 *
 * 触らないことが重要で、同じ文字列でも `textContent` へ代入するとテキストノードが作り直され、
 * キャレットも IME の未確定文字も消える。他人の編集が届くたびに自分の入力が飛ぶ、という壊れ方は
 * これが原因になる。
 */
export function setRowText(el: HTMLElement, text: string): boolean {
  if ((el.textContent ?? "") === text) return false;
  const offset = caretOffset(el);
  const node = textNodeOf(el);
  node.data = text;
  if (offset != null) setCaret(el, Math.min(offset, text.length));
  return true;
}
